import {
  FC,
  PropsWithChildren,
  createContext,
  useCallback,
  useState
} from 'react';
import * as Yup from 'yup';
import { useHookMemo } from 'src/hooks/use-hook-memo';

export interface Settings {
  colorMode: string;
}

export interface SettingsContextType {
  settings: Settings;
  changeSetting: (key: keyof Settings, value: string) => void;
}

export const SettingsContext = createContext<SettingsContextType | undefined>(
  undefined
);

const SETTINGS_KEY = 'deinverband:settings';

const validateSettings = Yup.object({
  colorMode: Yup.string().oneOf(['light', 'dark', 'auto']).default('light')
});

const deserializeSettings = () => {
  try {
    return JSON.parse(localStorage.getItem(SETTINGS_KEY) || '{}');
  } catch {
    return {};
  }
};

const serializeSettings = (settings: Settings) => {
  return localStorage.setItem(SETTINGS_KEY, JSON.stringify(settings));
};

/**
 * Provide local storage access
 */
export const SettingsProvider: FC<PropsWithChildren> = (props) => {
  const { children } = props;

  const [settings, setSettings] = useState<Settings>(
    validateSettings.cast(deserializeSettings())
  );

  const changeSetting = useCallback(
    (key: keyof Settings, value: string) => {
      const update = { ...settings, [key]: value };
      serializeSettings(update);

      // trigger re-render by updating local state
      setSettings(update);
    },
    [settings]
  );

  const context = useHookMemo({
    settings,
    changeSetting
  });

  return (
    <SettingsContext.Provider value={context}>
      {children}
    </SettingsContext.Provider>
  );
};
